import { UNHANDLED_ERROR_MESSAGE } from "components/organisms/ShipmentErrors/ShipmentErrors.utils";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { QuerySort } from "interfaces/api.interface";
import { IShipment } from "models/shipment/shipment.model";
import { toast } from "react-hot-toast";
import {
  fetchShipments,
  fetchAllTrackingStatuses,
  fetchAllIntegrationStores,
  fetchAllCarriers,
  CarrierFilterOption,
} from "services/api/shipments";
import { AppThunk, RootState } from "store";
import {
  setData,
  setQueryBulkShipment,
  setQueryFromAddressCountryIso,
  setQueryFromDate,
  setQueryDateRangeField,
  setQueryToDate,
  setQueryLabelJob,
  setQueryOffset,
  setQuerySearch,
  setQuerySort,
  setQueryToAddressCountryIso,
  setStatus,
  setQueryTrackingStatus,
  setTrackingStatuses,
  setAllIntegrationStores,
  setAllIntegrationStoresLoadingStatus,
  setAllCarriers,
  setAllCarriersLoadingStatus,
  setQueryStoreName,
  setQueryShipmentSourceName,
  setQueryCarrierName,
  setQueryCarrierServices,
  setQueryShipmentStatuses,
  setQueryStoreUpdateStatuses,
  setQuerySku,
  setQueryQuantity,
  setQueryItemQuantityGreaterThan,
  setQueryItemQuantityLessThan,
  setDateRangeDays,
  setQueryDescription,
} from "../index.slice";
import { IShipmentsSlice } from "../ShipmentsSlice.interface";
import { calculateDaysBetweenDates } from "utils/calculate/calculateDaysBetweenDates.util";
import { adjustDateRangeToLocalDay } from "utils/adjustDateRangeToLocalDay";
import dayjs from "dayjs";
import { statuses as ShipmentStatus } from "enum/shipment-statuses.enum";

export const searchShipmentsThunk = (query: string): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQuerySearch(query));

    dispatch(fetchShipmentsThunk());
  };
};

export const changePageThunk = (page: number): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryOffset(page));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeSortOrderThunk = (sort: QuerySort<IShipment>): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQuerySort(sort));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeBulkUploadFilterThunk = (
  bulkUpload: IShipmentsSlice["query"]["filters"]["bulkShipment"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryBulkShipment(bulkUpload));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeFromCountryIsoFilterThunk = (
  countryIso: IShipmentsSlice["query"]["filters"]["fromAddressCountryIso"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryFromAddressCountryIso(countryIso));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeTrackingStatusFilterThunk = (
  trackingStatus: IShipmentsSlice["query"]["filters"]["trackingStatus"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryTrackingStatus(trackingStatus));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeStoreNameFilterThunk = (
  storeName: IShipmentsSlice["query"]["filters"]["storeName"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryStoreName(storeName));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeSkuFilterThunk = (
  sku: IShipmentsSlice["query"]["filters"]["sku"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQuerySku(sku));
  };
};

export const changeDescriptionFilterThunk = (
  description: IShipmentsSlice["query"]["filters"]["description"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryDescription(description));
  };
};

export const changeQuantityFilterThunk = (
  quantity: IShipmentsSlice["query"]["filters"]["quantity"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryQuantity(quantity));
  };
};

export const changeItemQuantityRangeFilterThunk = (
  greaterThan: IShipmentsSlice["query"]["filters"]["itemQuantityGreaterThan"],
  lessThan: IShipmentsSlice["query"]["filters"]["itemQuantityLessThan"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryItemQuantityGreaterThan(greaterThan));
    await dispatch(setQueryItemQuantityLessThan(lessThan));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeIntegrationFilterThunk = (
  shipmentSourceName: IShipmentsSlice["query"]["filters"]["shipmentSourceName"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryShipmentSourceName(shipmentSourceName));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeCarrierFilterThunk = (
  carrierName: IShipmentsSlice["query"]["filters"]["carrierName"],
  carrierServices: IShipmentsSlice["query"]["filters"]["carrierServices"] = null,
): AppThunk => {
  // Synchronous on purpose: both halves of the selection must land in the store
  // before the fetch reads them, or a carrier and its services disagree.
  return (dispatch) => {
    dispatch(setQueryCarrierName(carrierName));
    dispatch(setQueryCarrierServices(carrierServices));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeShipmentStatusesFilterThunk = (
  shipmentStatuses: IShipmentsSlice["query"]["filters"]["shipmentStatuses"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryShipmentStatuses(shipmentStatuses));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeStoreUpdateStatusesFilterThunk = (
  storeUpdateStatuses: IShipmentsSlice["query"]["filters"]["storeUpdateStatuses"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryStoreUpdateStatuses(storeUpdateStatuses));
    dispatch(fetchShipmentsThunk());
  };
};

/** Clears both sections of the Status pill with a single refetch. Both
 * dispatches are synchronous on purpose so the panel clears in one render. */
export const clearStatusPillFiltersThunk = (): AppThunk => {
  return async (dispatch) => {
    dispatch(setQueryShipmentStatuses(null));
    dispatch(setQueryStoreUpdateStatuses(null));
    dispatch(fetchShipmentsThunk());
  };
};

export const clearFiltersThunk = (): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryTrackingStatus(null));
    await dispatch(setQueryBulkShipment(null));
    await dispatch(setQueryFromAddressCountryIso(null));
    await dispatch(setQueryToAddressCountryIso(null));
    await dispatch(setQueryLabelJob(null));
    await dispatch(setQueryStoreName(null));
    dispatch(fetchShipmentsThunk());
  };
};

export const applyDateRangeDaysThunk = (
  dateRangeDay: "1d" | "2d" | "7d" | "30d" | "1y" | "None",
): AppThunk => {
  // Synchronous on purpose: callers rely on the store being fully updated by
  // the time dispatch returns (e.g. a fetch later in the same effect pass
  // reads these dates via getState()).
  return (dispatch) => {
    dispatch(setDateRangeDays(dateRangeDay));
    let fromDate = new Date();
    const toDate = new Date();
    const isLeapYear = (year) => new Date(year, 1, 29).getMonth() === 1;
    if (dateRangeDay === "1d") {
      fromDate = new Date();
    } else if (dateRangeDay === "2d") {
      fromDate.setDate(fromDate.getDate() - 1);
    } else if (dateRangeDay === "7d") {
      fromDate.setDate(fromDate.getDate() - 7);
    } else if (dateRangeDay === "30d") {
      fromDate.setDate(fromDate.getDate() - 30);
    } else if (dateRangeDay === "1y") {
      fromDate.setDate(
        fromDate.getDate() - (isLeapYear(toDate.getFullYear()) ? 366 : 365),
      );
    }
    dispatch(setQueryFromDate(fromDate.toISOString()));
    dispatch(setQueryToDate(toDate.toISOString()));
  };
};

export const changeDateRangeDaysThunk = (
  dateRangeDay: "1d" | "2d" | "7d" | "30d" | "1y" | "None",
): AppThunk => {
  return async (dispatch) => {
    dispatch(applyDateRangeDaysThunk(dateRangeDay));
    dispatch(fetchShipmentsThunk());
  };
};

export const changeDateRangeFilterThunk = (
  fromDate: IShipmentsSlice["query"]["filters"]["fromDate"],
  toDate: IShipmentsSlice["query"]["filters"]["toDate"],
): AppThunk => {
  return async (dispatch) => {
    let from = fromDate;
    let to = toDate;
    //prevents people from setting the from after the to
    if (toDate < fromDate) {
      from = toDate;
      to = fromDate;
    }
    await dispatch(setQueryFromDate(from));
    await dispatch(setQueryToDate(to));
    const daysBetweenDates = calculateDaysBetweenDates(
      new Date(from),
      new Date(to),
    );

    let dateRange: "1d" | "2d" | "7d" | "30d" | "1y" | "None" = "None";

    const isToday = () => {
      return (
        dayjs(new Date()).format("DD/MM/YYYY") ===
        dayjs(new Date(from)).format("DD/MM/YYYY")
      );
    };

    const isYesterday = () => {
      const today = new Date();

      // Set the date to yesterday by subtracting one day
      const yesterday = today.setDate(today.getDate() - 1);

      return (
        dayjs(new Date(yesterday)).format("DD/MM/YYYY") ===
        dayjs(new Date(from)).format("DD/MM/YYYY")
      );
    };

    if (daysBetweenDates === 0 && isToday()) {
      dateRange = "1d";
    } else if (daysBetweenDates === 1 && isYesterday()) {
      dateRange = "2d";
    } else if (daysBetweenDates === 7) {
      dateRange = "7d";
    } else if (daysBetweenDates === 30) {
      dateRange = "30d";
    }
    // else if (daysBetweenDates === (isLeapYear(new Date()) ? 366 : 365)) {
    //   dateRange = "1y";
    // }
    dispatch(fetchShipmentsThunk());
    dispatch(setDateRangeDays(dateRange));
  };
};

export const changeDateRangeFieldThunk = (
  dateRangeField: IShipmentsSlice["query"]["filters"]["dateRangeField"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryDateRangeField(dateRangeField));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeToCountryIsoFilterThunk = (
  countryIso: IShipmentsSlice["query"]["filters"]["toAddressCountryIso"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryToAddressCountryIso(countryIso));

    dispatch(fetchShipmentsThunk());
  };
};

export const changeLabelJobFilterThunk = (
  labelJob: IShipmentsSlice["query"]["filters"]["labelJob"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setQueryLabelJob(labelJob));

    dispatch(fetchShipmentsThunk());
  };
};

// The filter option lists (tracking statuses, integration stores, carriers)
// are dispatched from several mount points (the Shipments page, the nav sync
// dropdown, the report modal), and page remounts re-run those effects. The
// data only changes when the selected sales account changes, so each list is
// fetched at most once per account: repeat dispatches for the same account
// no-op instead of refiring an identical request. Module scope rather than
// slice state so a full page reload naturally starts fresh; a failed fetch
// clears its marker so the next dispatch retries.
const optionListFetchedFor = {
  trackingStatuses: null as string | null,
  integrationStores: null as string | null,
  carriers: null as string | null,
};

export const fetchTrackingStatusesThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    // No selected sales account (e.g. itd-staff) means nothing to scope to.
    // Skip the call (a null UUID would 400) and show no tracking statuses.
    if (!salesAccountUUID) {
      dispatch(setTrackingStatuses([]));
      return;
    }

    if (optionListFetchedFor.trackingStatuses === salesAccountUUID) return;
    optionListFetchedFor.trackingStatuses = salesAccountUUID;

    try {
      const { data } = await fetchAllTrackingStatuses(salesAccountUUID);
      dispatch(setTrackingStatuses(data ?? []));
    } catch (error) {
      optionListFetchedFor.trackingStatuses = null;
      throw error;
    }
  };
};

export const fetchIntegrationStoresThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    // itd-staff (and anyone) without a selected sales account have no stores to
    // pull. Requesting with a null UUID hits /sales-accounts/null/... which the
    // API rejects with a 400 — so skip the call and just show no stores.
    if (!salesAccountUUID) {
      dispatch(setAllIntegrationStores([]));
      return;
    }

    if (optionListFetchedFor.integrationStores === salesAccountUUID) return;
    optionListFetchedFor.integrationStores = salesAccountUUID;

    dispatch(setAllIntegrationStoresLoadingStatus(ApiLoadingStatus.LOADING));
    try {
      const { data } = await fetchAllIntegrationStores(salesAccountUUID);
      dispatch(setAllIntegrationStores(data ?? []));
      dispatch(setAllIntegrationStoresLoadingStatus(ApiLoadingStatus.SUCCESS));
    } catch (error) {
      optionListFetchedFor.integrationStores = null;
      dispatch(setAllIntegrationStoresLoadingStatus(ApiLoadingStatus.FAILED));
    }
  };
};

/**
 * The carriers endpoint returns each carrier with its services, but a server
 * that predates that change returns plain carrier names. Both are accepted so
 * the two deploys can land in either order: against the older shape there are
 * simply no services to pick, and the filter behaves exactly as it used to.
 */
const normalizeCarrierOptions = (
  data: CarrierFilterOption[] | string[] | undefined,
): IShipmentsSlice["allCarriers"] => {
  return (data ?? [])
    .map((entry) =>
      typeof entry === "string"
        ? { carrierName: entry, services: [] }
        : {
            carrierName: entry?.carrierName ?? "",
            services: entry?.services ?? [],
          },
    )
    .filter((carrier) => !!carrier.carrierName.trim());
};

export const fetchCarriersThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    // No selected sales account means there's no whitelist to read carriers
    // from — skip the call (a null UUID would 400) and show no carriers.
    if (!salesAccountUUID) {
      dispatch(setAllCarriers([]));
      return;
    }

    if (optionListFetchedFor.carriers === salesAccountUUID) return;
    optionListFetchedFor.carriers = salesAccountUUID;

    dispatch(setAllCarriersLoadingStatus(ApiLoadingStatus.LOADING));
    try {
      const { data } = await fetchAllCarriers(salesAccountUUID);
      dispatch(setAllCarriers(normalizeCarrierOptions(data)));
      dispatch(setAllCarriersLoadingStatus(ApiLoadingStatus.SUCCESS));
    } catch (error) {
      optionListFetchedFor.carriers = null;
      dispatch(setAllCarriersLoadingStatus(ApiLoadingStatus.FAILED));
    }
  };
};

// Refetches that do not bring their own controller (Pusher, modals, filter
// changes) share this one: the newest request wins and the previous in-flight
// one is aborted, so identical refetches never queue up on the server.
let sharedListController: AbortController | null = null;

export const fetchShipmentsThunk = (
  controller?: AbortController,
  options?: { suppressErrorToast?: boolean },
): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
      shipmentsSlice,
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;
    if (!controller) {
      sharedListController?.abort("Superseded by a newer shipments fetch");
      sharedListController = new AbortController();
      controller = sharedListController;
    }
    dispatch(setStatus(ApiLoadingStatus.LOADING));
    const {
      offset,
      filters: {
        labelJob,
        bulkShipment,
        statuses,
        search,
        fromAddressCountryIso,
        toAddressCountryIso,
        fromDate,
        toDate,
        dateRangeField,
        trackingStatus,
        integrationStoreId,
        storeName,
        shipmentSourceName,
        carrierName,
        carrierServices,
        shipmentStatuses,
        storeUpdateStatuses,
        sku,
        description,
        quantity,
        itemQuantityGreaterThan,
        itemQuantityLessThan,
      },
      sort,
    } = shipmentsSlice.query;

    // Use local calendar date so users see their full day, not the UTC day
    const { adjustedFromDate, adjustedToDate } = adjustDateRangeToLocalDay(
      fromDate,
      toDate,
    );
    if (salesAccountUUID) {
      const response = await fetchShipments(
        salesAccountUUID,
        {
          offset,
          filters: {
            ...(statuses && { statuses }),
            // References can contain "#" (e.g. Shopify order names like #GG18523);
            // unencoded, the fragment would truncate every filter after it.
            ...(search && { search: encodeURIComponent(search) }),
            ...(bulkShipment && { bulkShipmentUUID: bulkShipment.uuid }),
            ...(labelJob && { labelJobUUID: labelJob.uuid }),
            ...(fromAddressCountryIso && { fromAddressCountryIso }),
            ...(toAddressCountryIso && { toAddressCountryIso }),
            ...(adjustedFromDate && { fromDate: adjustedFromDate }),
            ...(adjustedToDate && { toDate: adjustedToDate }),
            ...(dateRangeField === "label_generated_at" &&
              statuses?.[0] === ShipmentStatus.label_created && {
                dateRangeField,
              }),
            ...(trackingStatus && { trackingStatus }),
            ...(integrationStoreId && { integrationStoreId }),
            // Every selected store, as a JSON array: store names can contain
            // commas, so a comma-separated list is not safe.
            ...(storeName?.length && {
              storeNames: encodeURIComponent(JSON.stringify(storeName)),
            }),
            ...(sku && { sku: encodeURIComponent(sku) }),
            ...(description && {
              description: encodeURIComponent(description),
            }),
            ...(quantity && { quantity }),
            ...(itemQuantityGreaterThan && { itemQuantityGreaterThan }),
            ...(itemQuantityLessThan && { itemQuantityLessThan }),
            // Integration pill is multi-select: comma-separated source names.
            ...(shipmentSourceName?.length && {
              shipmentSourceName: shipmentSourceName.join(","),
            }),
            // The carrier filter is true multi-select: the server accepts a
            // comma-separated list of carrier names, each meaning every
            // service under it.
            ...(carrierName?.length && {
              carrierName: carrierName
                .map((name) => encodeURIComponent(name))
                .join(","),
            }),
            // Services picked one by one under a carrier that is not selected
            // whole. JSON because service names contain spaces, dashes and
            // colons ("DPD 10:30"), leaving no safe delimiter.
            ...(carrierServices?.length && {
              carrierServices: encodeURIComponent(
                JSON.stringify(carrierServices),
              ),
            }),
            // Status pill (ITD staff): both sections filter on state that
            // only exists for completed labels, so they are Complete-tab
            // only, like the columns they filter.
            ...(statuses?.[0] === ShipmentStatus.label_created &&
              shipmentStatuses?.length && {
                trackingStatuses: shipmentStatuses
                  .map((status) => encodeURIComponent(status))
                  .join(","),
              }),
            ...(statuses?.[0] === ShipmentStatus.label_created &&
              storeUpdateStatuses?.length && {
                storeUpdateStatuses: storeUpdateStatuses.join(","),
              }),
          },
          sort,
          limit: numberOfRowsSelected.value,
        },
        controller,
      );
      const { data, error, meta, message } = response;
      if (!error) {
        dispatch(
          setData({
            meta,
            data,
          }),
        );
        dispatch(setStatus(ApiLoadingStatus.SUCCESS));
      } else {
        // Check if the error is an AbortError (handles different error structures)
        const isAbortError =
          (typeof message === "object" &&
            message !== null &&
            (message as { name?: string }).name === "AbortError") ||
          (typeof message === "string" &&
            message.toLowerCase().includes("abort"));
        if (!isAbortError && !options?.suppressErrorToast) {
          toast.error(UNHANDLED_ERROR_MESSAGE);
        }
      }
    }
  };
};
